/**
 * WS-8 — Non-secret auth-UI hint.
 *
 * The frontend used to store the user JWT in ``localStorage`` under
 * ``stockaily_token`` and derive UI gating from its presence. WS-8
 * moves the actual authentication credential into an HttpOnly
 * server-side cookie so no JS-readable credential exists.
 *
 * Several UI surfaces still need a cheap "did I just authenticate?"
 * hint that survives a page refresh WITHOUT ever holding a secret.
 * ``stockaily_authed_hint`` is that hint: a boolean flag with NO
 * cryptographic value. If an XSS attacker reads it, they gain
 * nothing they could not already infer from ``/api/auth/me``.
 *
 * The authoritative source of authentication remains the cookie
 * session verified server-side via ``/api/auth/me``.
 */
const HINT_KEY = "stockaily_authed_hint";

export function setAuthHint() {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(HINT_KEY, "1");
  } catch {
    /* localStorage disabled — safe to ignore */
  }
}

export function clearAuthHint() {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(HINT_KEY);
    // Also proactively purge the retired credential key so upgrades
    // from older builds do not leave a JS-readable value behind.
    window.localStorage.removeItem("stockaily_token");
    if (window.sessionStorage) {
      window.sessionStorage.removeItem("stockaily_admin_token");
    }
  } catch {
    /* ignore */
  }
}

export function hasAuthHint() {
  try {
    if (typeof window === "undefined") return false;
    return !!window.localStorage.getItem(HINT_KEY);
  } catch {
    return false;
  }
}

/** One-shot legacy cleanup: on module load, drop any retired
 *  credential-shaped values that may linger from a pre-WS-8 build. */
export function purgeLegacyAuthCredentials() {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem("stockaily_token");
    if (window.sessionStorage) {
      window.sessionStorage.removeItem("stockaily_admin_token");
    }
    window.localStorage.removeItem("stockaily_admin_token");
  } catch {
    /* ignore */
  }
}
